import { supabase } from '../supabaseClient';
import type { SchoolNoticeRow } from '../types/schoolNotice';
import { buildListHeadVersion, loadSchoolNoticesCache, saveSchoolNoticesCache } from '../utils/platformGlobalAdminCache';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import {
  clearLoginNoticeContentCache,
  loadLoginNoticeContentCache,
  saveLoginNoticeContentCache,
} from '../utils/loginNoticeContentCache';

function mapRow(row: Record<string, unknown>): SchoolNoticeRow {
  return {
    id: String(row.id),
    workspace_id: row.workspace_id == null ? null : String(row.workspace_id),
    title: String(row.title ?? ''),
    body_html: String(row.body_html ?? ''),
    show_on_login: Boolean(row.show_on_login),
    created_by: row.created_by != null ? String(row.created_by) : null,
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

/**
 * 플랫폼 전역 공지 (`workspace_id IS NULL`). LS 캐시가 있으면 `id/updated_at` head만 먼저 비교하고,
 * 동일하면 본문(body_html 등) 재조회 없이 캐시를 재사용한다. `forceRefresh`는 head 비교도 건너뛰고 항상 풀 조회.
 */
export async function listSchoolNotices(options?: { forceRefresh?: boolean }): Promise<SchoolNoticeRow[]> {
  const cached = options?.forceRefresh ? null : loadSchoolNoticesCache();
  if (cached?.found && cached.version) {
    const { data: headRows, error: headError } = await supabase
      .from('school_notices')
      .select('id, updated_at')
      .is('workspace_id', null);
    if (!headError) {
      const headVersion = buildListHeadVersion(
        (headRows ?? []).map((r) => ({ id: String((r as Record<string, unknown>).id), updated_at: String((r as Record<string, unknown>).updated_at ?? '') }))
      );
      if (headVersion === cached.version) {
        if (process.env.NODE_ENV === 'development') {
          recordDevLocalStorageProgramCacheUse('플랫폼공지·목록·LS(head일치)');
        }
        return cached.rows;
      }
    }
  }
  const { data, error } = await supabase
    .from('school_notices')
    .select('id, workspace_id, title, body_html, show_on_login, created_by, created_at, updated_at')
    .is('workspace_id', null)
    .order('updated_at', { ascending: false });
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r) => mapRow(r as Record<string, unknown>));
  saveSchoolNoticesCache(rows, { force: true, version: buildListHeadVersion(rows) });
  return rows;
}

/**
 * 로그인 팝업: 플랫폼 전역 «적용 중» 공지 — 최신 1건, show_on_login 일 때만.
 * id+updated_at이 캐시와 같으면 본문은 LS에서 재사용.
 */
export async function listSchoolNoticesForLoginPopup(): Promise<SchoolNoticeRow[]> {
  const { data: metaRows, error: metaError } = await supabase
    .from('school_notices')
    .select('id, updated_at, show_on_login')
    .is('workspace_id', null)
    .order('updated_at', { ascending: false })
    .limit(1);
  if (metaError) throw new Error(metaError.message);

  const meta = (metaRows ?? [])[0] as Record<string, unknown> | undefined;
  if (!meta || !Boolean(meta.show_on_login)) return [];

  const id = String(meta.id ?? '').trim();
  const updatedAt = String(meta.updated_at ?? '').trim();
  if (!id) return [];

  const cached = loadLoginNoticeContentCache();
  if (cached && cached.id === id && cached.updated_at === updatedAt) {
    if (process.env.NODE_ENV === 'development') {
      recordDevLocalStorageProgramCacheUse('로그인공지·본문·LS');
    }
    return [{ ...cached.row, show_on_login: true, updated_at: updatedAt }];
  }

  const { data, error } = await supabase
    .from('school_notices')
    .select('id, workspace_id, title, body_html, show_on_login, created_by, created_at, updated_at')
    .eq('id', id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return [];
  const row = mapRow(data as Record<string, unknown>);
  if (!row.show_on_login) return [];
  saveLoginNoticeContentCache(row);
  return [row];
}

export async function createSchoolNotice(payload: {
  title: string;
  body_html: string;
  show_on_login: boolean;
}): Promise<SchoolNoticeRow> {
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData.user?.id ?? null;
  const { data, error } = await supabase
    .from('school_notices')
    .insert({
      workspace_id: null,
      title: payload.title.trim(),
      body_html: payload.body_html,
      show_on_login: payload.show_on_login,
      created_by: uid,
    })
    .select('id, workspace_id, title, body_html, show_on_login, created_by, created_at, updated_at')
    .single();
  if (error) throw new Error(error.message);
  const created = mapRow(data as Record<string, unknown>);
  const cached = loadSchoolNoticesCache();
  const prev = cached.found ? cached.rows : [];
  const nextRows = [created, ...prev.filter((row) => row.id !== created.id)];
  saveSchoolNoticesCache(nextRows, { force: true, version: buildListHeadVersion(nextRows) });
  if (created.show_on_login) saveLoginNoticeContentCache(created);
  return created;
}

export async function updateSchoolNotice(
  noticeId: string,
  payload: { title: string; body_html: string; show_on_login: boolean }
): Promise<SchoolNoticeRow> {
  const { data, error } = await supabase
    .from('school_notices')
    .update({
      title: payload.title.trim(),
      body_html: payload.body_html,
      show_on_login: payload.show_on_login,
      updated_at: new Date().toISOString(),
    })
    .eq('id', noticeId)
    .select('id, workspace_id, title, body_html, show_on_login, created_by, created_at, updated_at')
    .single();
  if (error) throw new Error(error.message);
  const updated = mapRow(data as Record<string, unknown>);
  const cached = loadSchoolNoticesCache();
  if (cached.found) {
    const nextRows = cached.rows.map((row) => (row.id === updated.id ? updated : row));
    saveSchoolNoticesCache(nextRows, { force: true, version: buildListHeadVersion(nextRows) });
  }
  if (updated.show_on_login) saveLoginNoticeContentCache(updated);
  return updated;
}

export async function deleteSchoolNotice(noticeId: string): Promise<void> {
  const { error } = await supabase.from('school_notices').delete().eq('id', noticeId);
  if (error) throw new Error(error.message);
  const cached = loadSchoolNoticesCache();
  if (cached.found) {
    const nextRows = cached.rows.filter((row) => row.id !== noticeId);
    saveSchoolNoticesCache(nextRows, { force: true, version: buildListHeadVersion(nextRows) });
  }
  const loginCached = loadLoginNoticeContentCache();
  if (loginCached?.id === noticeId) {
    clearLoginNoticeContentCache();
  }
}
